<script setup>
/**
 * Forms — a fill-in card that opens inside WhatsApp instead of the assistant asking one
 * question at a time. Meta calls them Flows; this screen calls them forms (see the FORMS
 * block in `i18n.js` for why).
 *
 * WAS `pages/FlowsPage.vue`, WHICH IS GONE. Its nav row went with it, for the reason that
 * row already gave for sitting next to Approved messages: both are one WhatsApp number's
 * inventory at Meta, reviewed by Meta before it works. This is the second tab of
 * `TemplatesPage`, reached as `…/waslo/templates?tab=forms`.
 *
 * ── WHY THIS TAB OWNS ITS READS AND `MenusTab` DOES NOT ────────────────────────────────
 *
 * The two tabs in this change are built differently on purpose, and the difference is not
 * style:
 *
 *   • `MenusTab` is one cheap BFF list, and its ROW COUNT decides what the page header's
 *     primary button says. So the page owns the read.
 *   • This tab makes THREE reads, one of which goes out to Meta's Graph API through Rails
 *     for a WABA that rate-limits it, and it has no primary button at all — §3.2's "the
 *     Forms tab authors nothing". Doing that work on every visit to Approved messages, for
 *     an owner who came to fix a template, would be a real cost for nothing. So `TemplatesPage`
 *     mounts this with `v-if`, the reads happen the first time the tab is opened, and the
 *     states they can be in are rendered HERE rather than by the page frame.
 *
 * The one read that is genuinely duplicated is the sender list, which the page holds too.
 * It is Chatwoot's own `/inboxes`, it is small, and paying for it twice is what keeps this
 * component mountable on its own — which is how `specs/flows.spec.js` tests it.
 *
 * ── THE FOUR QUESTIONS AN OWNER OPENS THIS TAB WITH ────────────────────────────────
 *
 *   1. Can my number do this?        → the verdict card, from signals the platform already polls.
 *   2. What do I already have?       → Meta's own list, with Meta's own status on each row.
 *   3. Which of my tasks could?      → the task list, only tasks written as steps.
 *   4. What would stay in the chat?  → the preview, per task, with the reason for each step.
 *
 * The tab answers them in that order, and nothing below question 1 pretends to work when the
 * answer to it is no: the publish button is disabled beside the reason rather than hidden, so
 * "not yet, and here is why" cannot be misread as "this feature does not exist".
 *
 * ── WHERE EACH HALF COMES FROM, AND WHY IT READS TWO SERVERS ───────────────────────
 *
 * CHATWOOT (`api/flows.js`) owns the number: the WABA id and the Graph token live on the channel
 * row and nowhere else, so eligibility, Meta's list and the publish all go there.
 * THE AUTHORING SERVICE (`api/client.js`) owns the tasks: they live in `waslo_ai`. One screen
 * reading two servers is the honest shape here — the alternative is a Chatwoot endpoint that
 * proxies a task list, which is a second place for the list to be stale.
 *
 * ── THE TASK IDENTIFIER, AND WHY IT IS A NUMBER ───────────────────────────────────
 *
 * The form builder addresses a task by its MACHINE NAME, and this screen cannot know it: the
 * authoring BFF invents that name and deliberately never ships it (`api/client.js:1108`, `:2605`).
 * So the numeric task id goes on the wire — the builder accepts either — and nothing on this side
 * slugifies anything. A task the builder cannot find comes back as `no_such_task`, which reads as
 * our wiring problem rather than as something the owner did.
 */
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import FlowEligibilityCard from './FlowEligibilityCard.vue';
import FlowTaskRow from './FlowTaskRow.vue';
import WasloExplainer from './WasloExplainer.vue';
import WasloSelect from './WasloSelect.vue';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { useWasloApi } from '../composables/useWasloApi.js';
import { flowErrorSentence, flowStatusLabel } from '../helpers/flow-error.js';
import { formStanding } from '../helpers/form-standing.js';
import { useWasloI18n } from '../i18n.js';
import { flowsApi } from '../api/flows.js';
import { wasloApi } from '../api/client.js';

const { t } = useWasloI18n();
const accountId = useWasloAccountId();
const router = useRouter();

/** P-4.10: Arrange form lives under the task it edits. */
const arrange = task => {
  if (accountId.value === null) return;
  router.push({
    name: 'waslo_task_form',
    params: { accountId: String(accountId.value), taskId: String(task.id) },
  });
};

// `isEmpty: () => false`: a workspace with no WhatsApp number is a MEANING, rendered under the
// explanation, not a blank frame. Same call the templates page makes.
const senders = useWasloApi(
  signal => flowsApi.senders(accountId.value, signal),
  { isEmpty: () => false }
);

// Only tasks written as steps can become a form — the builder reads the step graph. A task
// without one is not an error and not a smaller answer, it is simply not a candidate, so it is
// filtered out here rather than listed as a row that cannot be opened.
const tasks = useWasloApi(
  async signal => {
    const all = await wasloApi.getTasks(accountId.value, signal);
    return all.filter(task => task.hasGraph === true);
  },
  { isEmpty: () => false }
);

const senderId = ref('');
const eligibility = ref(null);
// Why the verdict is missing, when it is. Distinct from `eligibility === null` alone, because
// "we have not asked yet" and "we asked and could not get an answer" are different screens.
const verdictError = ref('');
const flows = ref([]);
const isReading = ref(false);
const readFailed = ref(false);
const listNote = ref('');
/** F3.2: false when WhatsApp holds more forms than the list could read. */
const flowsComplete = ref(true);
/** P-4.8: what the controller says is recorded on this number; null = could not check. */
const publishedForms = ref(null);
/** Keyed by task id: the language whose "Try it" is opening. */
const trying = ref({});

/** Keyed by task id: the preview, the in-flight flags, the sentence, the note. */
const previews = ref({});
const previewing = ref({});
const publishing = ref({});
const taskErrors = ref({});
const publishedNotes = ref({});

const senderOptions = computed(() =>
  (senders.data.value ?? []).map(sender => ({
    value: String(sender.id),
    label: sender.phoneNumber
      ? `${sender.name} · ${sender.phoneNumber}`
      : sender.name,
  }))
);

// The first number, chosen for her. One number is the common case and a picker she has to touch
// before anything appears reads as an empty screen.
watch(
  senderOptions,
  options => {
    if (senderId.value === '' && options.length) {
      senderId.value = options[0].value;
    }
  },
  { immediate: true }
);

// STRICTLY the server's own yes. A missing verdict is not a yes, and the button stays disabled —
// the server refuses either way, so this only decides what the owner sees before she presses.
const canPublish = computed(() => eligibility.value?.eligible === true);

/**
 * WHY the row's Publish button is off, when the reason is one she has to go and act on elsewhere.
 *
 * Only for a business Meta has not verified (2026-10-04): the fix is paperwork in Meta's own
 * settings and takes days, so a disabled button with no word beside it reads as broken. The card
 * above carries the steps; this is one line pointing at it. Every other refusal keeps the button
 * as it was, disabled beside the card's own sentence.
 */
const publishBlockedReason = computed(() =>
  eligibility.value?.eligible !== true &&
  eligibility.value?.code === 'business_not_verified'
    ? t('WASLO.FORMS.TASKS.PUBLISH_BLOCKED_BUSINESS')
    : ''
);

const rows = computed(() =>
  flows.value.map(flow => ({
    ...flow,
    statusLabel: flowStatusLabel(t, flow.status),
    firstProblem:
      flow.validationErrors.length > 0
        ? (flow.validationErrors[0]?.message ??
          flow.validationErrors[0]?.error_type ??
          '')
        : '',
  }))
);

const readFlows = async () => {
  if (senderId.value === '' || accountId.value === null) return;
  isReading.value = true;
  readFailed.value = false;
  listNote.value = '';
  const asked = senderId.value;
  try {
    const answer = await flowsApi.list(accountId.value, asked);
    // The number may have changed while this was in flight. A list routed back to the wrong
    // sender is the exact wrong answer this screen is scoped to avoid.
    if (asked !== senderId.value) return;
    eligibility.value = answer.eligibility;
    verdictError.value = answer.eligibility
      ? ''
      : flowErrorSentence(t, { serverCode: answer.builderError });
    flows.value = answer.flows;
    flowsComplete.value = answer.complete !== false;
    publishedForms.value = answer.publishedForms;
    // Meta refused the LIST. Saying so is the difference between "you have no forms" and "we
    // could not ask" — opposite facts about the same empty area.
    if (answer.metaError) {
      listNote.value = t('WASLO.FORMS.UNAVAILABLE');
    }
  } catch {
    if (asked !== senderId.value) return;
    eligibility.value = null;
    verdictError.value = '';
    flows.value = [];
    flowsComplete.value = true;
    publishedForms.value = null;
    readFailed.value = true;
  } finally {
    if (asked === senderId.value) isReading.value = false;
  }
};

watch(senderId, () => {
  flows.value = [];
  flowsComplete.value = true;
  publishedForms.value = null;
  eligibility.value = null;
  verdictError.value = '';
  previews.value = {};
  taskErrors.value = {};
  publishedNotes.value = {};
  readFlows();
});

/**
 * The language the PREVIEW panel is of.
 *
 * English, and stated rather than left to the server's default: a task's two forms carry
 * different boxes, so "the preview" is only meaningful once it says which one it is. Publishing
 * does BOTH languages regardless of what is previewed — see `publish` — so this decides what the
 * owner looks at, never what she gets.
 */
const PREVIEW_LANG = 'en';

/**
 * The two languages a form is published in, in the order the note names them.
 *
 * Each carries its own i18n key as a LITERAL rather than one built from the code, so every
 * sentence this screen can show is greppable and the translation checker can see them.
 */
const PUBLISH_LANGS = [
  { code: 'en', label: 'WASLO.FORMS.LANG.EN' },
  { code: 'ar', label: 'WASLO.FORMS.LANG.AR' },
];

const preview = async task => {
  const key = String(task.id);
  previewing.value = { ...previewing.value, [key]: true };
  taskErrors.value = { ...taskErrors.value, [key]: '' };
  try {
    const form = await flowsApi.preview(
      accountId.value,
      senderId.value,
      key,
      PREVIEW_LANG
    );
    previews.value = { ...previews.value, [key]: form };
  } catch (error) {
    taskErrors.value = {
      ...taskErrors.value,
      [key]: flowErrorSentence(t, error),
    };
    // `no_form` is a real answer about the task and the server sends the reasons with it, so the
    // panel still opens — it is the one refusal that has something to show.
    if (error?.form) {
      previews.value = {
        ...previews.value,
        [key]: {
          hasForm: false,
          fieldCount: 0,
          fields: [],
          stayInChat: (error.form.stay_in_chat ?? []).map(step => ({
            stepId: step?.step_id ?? null,
            name: step?.name ?? '',
            reason: step?.reason ?? null,
          })),
          definitionVersion: error.form.definition_version ?? null,
          name: null,
        },
      };
    }
  } finally {
    previewing.value = { ...previewing.value, [key]: false };
  }
};

const hide = task => {
  const next = { ...previews.value };
  delete next[String(task.id)];
  previews.value = next;
};

/**
 * ONE language's publish: the note it earned, or the sentence explaining why it has none.
 *
 * Neither outcome throws. A `no_form` refusal is a real answer about the task — "nothing in it
 * can become a box in this language yet" — and an owner who has written her English questions
 * and not her Arabic ones must get her English form AND that sentence, not an error instead of
 * both.
 */
const publishOne = async (key, lang) => {
  try {
    const flow = await flowsApi.publish(
      accountId.value,
      senderId.value,
      key,
      lang.code
    );
    return {
      // NO STATUS IS CLAIMED. Meta says nothing about the state on a publish, so the note names
      // the form and sends her back to WhatsApp's own list for the verdict.
      note: t('WASLO.FORMS.TASKS.PUBLISHED_NOTE', { name: flow.name }),
      // Published at WhatsApp and NOT reachable by a customer: the id never got back to the
      // assistant. Said plainly, because the alternative is a form that exists, is listed, and
      // is never sent — with nothing anywhere explaining the silence.
      problem: flow.sendable
        ? ''
        : t('WASLO.FORMS.TASKS.NOT_SENDABLE', { name: flow.name }),
    };
  } catch (error) {
    return {
      note: '',
      problem:
        error?.serverCode === 'no_form'
          ? t('WASLO.FORMS.TASKS.NO_FORM_IN_LANG', { language: t(lang.label) })
          : flowErrorSentence(t, error),
    };
  }
};

/**
 * PUBLISH BOTH LANGUAGES, one call each, in order.
 *
 * A form is not a translation of another form — the builder refuses to label a box in a language
 * its question is not written in — so an English and an Arabic form are two artefacts at Meta
 * with two names and two ids. Publishing only one is how an Arabic customer never sees a form at
 * all, which is what this screen did.
 *
 * SEQUENTIAL, not `Promise.all`: each publish is three Graph round trips against the same WABA,
 * and Meta rate-limits flow creation per business account. The chained `reduce` is what keeps
 * them in order without a loop.
 */
/** `langs` is which languages to publish: both for a first publish, one for an Update (P-4.8). */
const publish = async (task, langs = PUBLISH_LANGS.map(lang => lang.code)) => {
  const key = String(task.id);
  const asked = PUBLISH_LANGS.filter(lang => langs.includes(lang.code));
  publishing.value = { ...publishing.value, [key]: true };
  taskErrors.value = { ...taskErrors.value, [key]: '' };
  publishedNotes.value = { ...publishedNotes.value, [key]: '' };
  try {
    const outcomes = await asked.reduce(
      (chain, lang) =>
        chain.then(async done => [...done, await publishOne(key, lang)]),
      Promise.resolve([])
    );
    publishedNotes.value = {
      ...publishedNotes.value,
      [key]: outcomes
        .map(o => o.note)
        .filter(Boolean)
        .join(' '),
    };
    taskErrors.value = {
      ...taskErrors.value,
      [key]: outcomes
        .map(o => o.problem)
        .filter(Boolean)
        .join(' '),
    };
  } finally {
    publishing.value = { ...publishing.value, [key]: false };
    await readFlows();
  }
};

/**
 * "Try it" (P-4.8): WhatsApp's own preview of a published form, in a new tab. The tab is opened
 * FIRST, inside the click, so a popup blocker lets it through; it is pointed at the link once
 * Meta answers, and closed again if Meta gives none.
 */
const tryForm = async (task, lang, flowId) => {
  const key = String(task.id);
  const tab = window.open('', '_blank');
  trying.value = { ...trying.value, [key]: lang };
  taskErrors.value = { ...taskErrors.value, [key]: '' };
  try {
    const { previewUrl } = await flowsApi.formPreview(
      accountId.value,
      senderId.value,
      flowId
    );
    if (tab) {
      tab.opener = null;
      tab.location.href = previewUrl;
    }
  } catch {
    tab?.close();
    taskErrors.value = {
      ...taskErrors.value,
      [key]: t('WASLO.FORMS.TASKS.STATE.TRY_FAILED'),
    };
  } finally {
    trying.value = { ...trying.value, [key]: '' };
  }
};
</script>

<template>
  <div class="flex flex-col gap-8 pb-10" data-waslo-forms-tab>
    <!--
      THE SENDER READ'S OWN THREE STATES, rendered here rather than by `WasloPageLayout`.
      A tab is not a page, so it cannot hand its states to the frame without deciding them
      for the tab beside it — the whole reason the frame is neutral while this one is open.
      The shapes are the frame's own, deliberately: loading is a centred spinner, a workspace
      with no assistant is NOT an error and gets no "Try again" (retrying cannot provision
      one), and a failure gets the sentence plus the retry.
    -->
    <div
      v-if="senders.isFetching.value"
      class="flex items-center justify-center py-10 text-n-slate-11"
      data-waslo-forms-tab-loading
    >
      <Spinner />
    </div>

    <div
      v-else-if="senders.isNotProvisioned.value"
      class="flex flex-col items-center justify-center gap-3 py-16 text-center"
      data-waslo-forms-tab-not-provisioned
    >
      <span class="i-lucide-bot-message-square size-6 text-n-slate-10" />
      <span class="text-base font-medium text-n-slate-12">
        {{ t('WASLO.COMMON.NOT_PROVISIONED_TITLE') }}
      </span>
      <p class="max-w-md text-sm text-n-slate-11">
        {{ t('WASLO.COMMON.NOT_PROVISIONED_BODY') }}
      </p>
    </div>

    <div
      v-else-if="senders.errorMessage.value"
      class="flex flex-col items-center justify-center gap-3 py-16 text-center"
      data-waslo-forms-tab-error
    >
      <span class="i-lucide-triangle-alert size-6 text-n-ruby-9" />
      <span class="text-base font-medium text-n-slate-12">
        {{ t('WASLO.COMMON.ERROR_TITLE') }}
      </span>
      <p class="max-w-md text-sm text-n-slate-11">
        {{ senders.errorMessage.value }}
      </p>
      <Button
        :label="t('WASLO.COMMON.RETRY')"
        variant="outline"
        color="slate"
        size="sm"
        data-waslo-forms-tab-retry
        @click="senders.reload()"
      />
    </div>

    <template v-else>
      <WasloExplainer
        icon="i-lucide-clipboard-list"
        :title="t('WASLO.FORMS.SPOTLIGHT_TITLE')"
        :note="t('WASLO.FORMS.SPOTLIGHT')"
      />

      <div
        v-if="senderOptions.length === 0"
        class="flex items-start w-full gap-4 px-6 py-6 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
        data-waslo-forms-no-sender
      >
        <span
          class="grid shrink-0 place-items-center size-9 rounded-xl bg-n-amber-3"
          aria-hidden="true"
        >
          <span class="size-5 i-lucide-phone-off text-n-amber-11" />
        </span>
        <div class="flex flex-col gap-1">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('WASLO.FORMS.NO_SENDER.TITLE') }}
          </p>
          <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
            {{ t('WASLO.FORMS.NO_SENDER.BODY') }}
          </p>
        </div>
      </div>

      <template v-else>
        <WasloSelect
          v-model="senderId"
          :label="t('WASLO.FORMS.SENDER_LABEL')"
          :options="senderOptions"
          data-waslo-forms-sender
        />

        <div
          v-if="isReading"
          class="flex items-center justify-center py-8 text-n-slate-11"
          data-waslo-forms-loading
        >
          <Spinner />
        </div>

        <p
          v-else-if="readFailed"
          class="mb-0 text-sm leading-6 text-n-ruby-9"
          data-waslo-forms-error
        >
          {{ t('WASLO.FORMS.UNAVAILABLE') }}
        </p>

        <template v-else>
          <FlowEligibilityCard v-if="eligibility" :eligibility="eligibility" />

          <div
            v-else
            class="flex items-start w-full gap-4 px-6 py-6 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
            data-waslo-forms-verdict-unavailable
          >
            <span
              class="grid shrink-0 place-items-center size-9 rounded-xl bg-n-slate-3"
              aria-hidden="true"
            >
              <span class="size-5 i-lucide-help-circle text-n-slate-11" />
            </span>
            <div class="flex flex-col gap-1">
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ t('WASLO.FORMS.VERDICT_UNAVAILABLE.TITLE') }}
              </p>
              <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
                {{ verdictError || t('WASLO.FORMS.VERDICT_UNAVAILABLE.BODY') }}
              </p>
            </div>
          </div>

          <section class="flex flex-col gap-4" data-waslo-forms-published>
            <SettingsHeader
              :heading="t('WASLO.FORMS.PUBLISHED.HEADING')"
              :description="t('WASLO.FORMS.PUBLISHED.DESCRIPTION')"
            />

            <Banner v-if="listNote" color="ruby" data-waslo-forms-list-note>
              {{ listNote }}
            </Banner>

            <!-- F3.2: how many WhatsApp holds, and whether there are more than the list read. -->
            <p
              v-if="rows.length > 0"
              class="mb-0 text-sm text-n-slate-11"
              data-waslo-forms-count
            >
              {{ t('WASLO.FORMS.PUBLISHED.COUNT', { count: rows.length }) }}
            </p>
            <p
              v-if="!flowsComplete"
              class="mb-0 text-sm text-n-amber-11"
              data-waslo-forms-more
            >
              {{ t('WASLO.FORMS.PUBLISHED.MORE') }}
            </p>

            <p
              v-if="rows.length === 0"
              class="mb-0 text-sm leading-6 text-n-slate-11"
              data-waslo-forms-published-empty
            >
              {{ t('WASLO.FORMS.PUBLISHED.EMPTY_BODY') }}
            </p>

            <ul v-else class="flex flex-col gap-3 mb-0 list-none">
              <li
                v-for="flow in rows"
                :key="flow.id ?? flow.name"
                class="flex flex-col gap-1 px-5 py-4 outline outline-1 -outline-offset-1 rounded-xl outline-n-container bg-n-solid-1"
                :data-waslo-forms-row="flow.name"
              >
                <div class="flex items-center justify-between gap-3">
                  <p class="mb-0 text-sm font-medium truncate text-n-slate-12">
                    {{ flow.name }}
                  </p>
                  <span
                    class="text-xs font-medium shrink-0 text-n-slate-11"
                    data-waslo-forms-row-status
                  >
                    {{ flow.statusLabel }}
                  </span>
                </div>
                <p
                  v-if="flow.legacy"
                  class="mb-0 text-xs leading-5 text-n-slate-11"
                  data-waslo-forms-row-legacy
                >
                  {{ t('WASLO.FORMS.PUBLISHED.LEGACY') }}
                </p>
                <p
                  v-if="flow.firstProblem"
                  class="mb-0 text-xs leading-5 text-n-ruby-9"
                  data-waslo-forms-row-problem
                >
                  {{
                    t('WASLO.FORMS.PUBLISHED.INVALID', {
                      detail: flow.firstProblem,
                    })
                  }}
                </p>
              </li>
            </ul>
          </section>

          <section class="flex flex-col gap-4" data-waslo-forms-tasks>
            <SettingsHeader
              :heading="t('WASLO.FORMS.TASKS.HEADING')"
              :description="t('WASLO.FORMS.TASKS.DESCRIPTION')"
            />

            <div
              v-if="tasks.isFetching.value"
              class="flex items-center justify-center py-8 text-n-slate-11"
              data-waslo-forms-tasks-loading
            >
              <Spinner />
            </div>

            <div
              v-else-if="(tasks.data.value ?? []).length === 0"
              class="flex flex-col gap-1"
              data-waslo-forms-tasks-empty
            >
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ t('WASLO.FORMS.TASKS.EMPTY_TITLE') }}
              </p>
              <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
                {{ t('WASLO.FORMS.TASKS.EMPTY_BODY') }}
              </p>
            </div>

            <ul v-else class="flex flex-col gap-3 mb-0 list-none">
              <FlowTaskRow
                v-for="task in tasks.data.value"
                :key="task.id"
                :task="task"
                :form="previews[String(task.id)] ?? null"
                :is-previewing="previewing[String(task.id)] === true"
                :is-publishing="publishing[String(task.id)] === true"
                :can-publish="canPublish"
                :blocked-reason="publishBlockedReason"
                :error-message="taskErrors[String(task.id)] ?? ''"
                :published-note="publishedNotes[String(task.id)] ?? ''"
                :standing="formStanding(publishedForms, task.id)"
                :trying-lang="trying[String(task.id)] ?? ''"
                @preview="preview(task)"
                @hide="hide(task)"
                @publish="langs => publish(task, langs)"
                @try="(lang, flowId) => tryForm(task, lang, flowId)"
                @arrange="arrange(task)"
              />
            </ul>
          </section>
        </template>
      </template>
    </template>
  </div>
</template>
